import { useEffect } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Button, Modal } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import { useProjectCreation } from "app/modules/project-from-comparison/context";
import { projectCreationDataAutomation } from "app/modules/project-from-comparison/dataAutomation";
import projectCreatingTrack from "app/modules/project-from-comparison/tracking";
import { ACTIVATION_HOME_ROUTE } from "app/pages/activation/routing/constants";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import * as SC from "./styles";
import {
    SUCCESS_MODAL_HEADER,
    SUCCESS_MODAL_BODY,
    SUCCESS_BUTTON_GO_TO_PROJECTS,
    SUCCESS_BUTTON_CONTINUE_HERE,
} from "./text";

type SuccessModalSchema = {
    isOpen: boolean;
    onClose: () => void;
};

export const SuccessModal = ({ isOpen, onClose }: SuccessModalSchema) => {
    const { projectName, resetContext } = useProjectCreation();
    const { translate } = useTranslate();

    const handleClose = () => {
        onClose();
        resetContext();

        projectCreatingTrack.trackProjectCreationSuccessContinueHereClick();
    };

    const goToProjects = () => {
        projectCreatingTrack.trackProjectCreationSuccessGoToProjectsClick();

        void proNavigator.go(ACTIVATION_HOME_ROUTE);
    };

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        projectCreatingTrack.trackProjectCreationSuccessShow();
    }, [isOpen]);

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={handleClose}
            shouldCloseOnEsc
            shouldCloseOnOverlayClick
            customStyles={{
                content: {
                    width: "400px",
                },
                wrapper: {
                    gap: "16px",
                },
            }}
            data-automation={projectCreationDataAutomation.successModal.container}
        >
            <Modal.Header>
                <Modal.Title data-automation={projectCreationDataAutomation.successModal.header}>
                    {translate(SUCCESS_MODAL_HEADER)}
                </Modal.Title>
            </Modal.Header>

            <SC.Container>
                <Text
                    fontVariant="body1"
                    color={colorsPalettes.carbon[400]}
                    data-automation={projectCreationDataAutomation.successModal.body}
                >
                    {translate(SUCCESS_MODAL_BODY, { projectName })}
                </Text>
            </SC.Container>

            <Modal.Footer>
                <Button
                    type="flat"
                    onClick={goToProjects}
                    dataAutomation={projectCreationDataAutomation.successModal.goToProjectsButton}
                >
                    {translate(SUCCESS_BUTTON_GO_TO_PROJECTS)}
                </Button>

                <Button
                    onClick={handleClose}
                    dataAutomation={projectCreationDataAutomation.successModal.continueHereButton}
                >
                    {translate(SUCCESS_BUTTON_CONTINUE_HERE)}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
